// ── Material Kit Dispatch Guard ──
// Blocks job assignment / scheduling when the JobSpec material kit is not
// ready. Mirrors the assertContractorAssignable shape used for Pay Profiles
// so every assignment path can compose them identically.
//
// Single source of truth: isKitGateCleared(kit). Do not duplicate the logic.
//
// Behavior:
//   • kit === undefined           → block ("Material Kit not generated.")
//   • kit incomplete, no override → block ("Material Kit incomplete.")
//   • kit fully confirmed         → allow
//   • kit override logged         → allow (override badge surfaced upstream)
//
// Out of scope (do NOT add): inventory sync, PO automation, vendor flows,
// substitutions, procurement, expiry/staleness enforcement.
//
// Job binding is required for a kit to exist. Jobs without a bound JobSpec
// can never have a kit; those callers should pass `kit = undefined` and let
// this guard surface the "not generated" message.

import { isKitGateCleared, isKitFullyConfirmed, type MaterialKit } from "./materialKit";

export interface KitDispatchGuardResult {
  allowed: boolean;
  reason: string;
  /** Plain-language toast title to show on block. */
  blockTitle: string;
  /** Plain-language toast description to show on block. */
  blockDescription: string;
  /** Discriminator for callers that want to render different inline UI. */
  state: "ok" | "ok_override" | "not_generated" | "incomplete";
}

const TITLE = "Material Kit not ready for dispatch.";

/**
 * Dispatch / assignment gate. Call this at every site that pins a contractor
 * (or internal crew) to a date or routes a job toward a truck roll. Pair
 * with `assertContractorAssignable` — both must pass.
 */
export function assertJobKitReady(args: {
  kit: MaterialKit | undefined;
}): KitDispatchGuardResult {
  const { kit } = args;

  if (!kit) {
    return {
      allowed: false,
      state: "not_generated",
      reason: "Material Kit not generated. Open Job Detail to initialize the kit.",
      blockTitle: TITLE,
      blockDescription:
        "Material Kit not generated. Open Job Detail to initialize the kit before scheduling.",
    };
  }

  if (isKitGateCleared(kit)) {
    return {
      allowed: true,
      state: kit.override ? "ok_override" : "ok",
      reason: kit.override
        ? `PM override logged (${kit.override.reason}).`
        : "All kit items confirmed.",
      blockTitle: TITLE,
      blockDescription: "",
    };
  }

  // Exists, not cleared → incomplete with no override.
  const total = kit.items.length;
  const pending = kit.items.filter((i) => i.status !== "confirmed").length;
  // Soft sanity for the "fully confirmed yet not cleared" impossibility —
  // keeps the predicate authoritative if isKitFullyConfirmed semantics shift.
  void isKitFullyConfirmed;

  return {
    allowed: false,
    state: "incomplete",
    reason: `Material Kit incomplete (${pending} of ${total} items unconfirmed).`,
    blockTitle: TITLE,
    blockDescription:
      "Material Kit incomplete. Confirm all items in the Material Kit panel, or log a PM override with a reason and notes (≥20 chars).",
  };
}
